// ═══════════════════════════════════════════════════════════════
// tax-presets — saved tax rates, named by the mechanic.
//
// A mobile mechanic crosses tax jurisdictions in a working day. The
// rate that applies is the one where the van is parked, not where the
// business is registered, and the same customer can sit in a
// different one next month.
//
// So this is deliberately NOT a list of states. A state list looks
// tidy and is wrong twice over: the rate that matters is usually
// state + county + city combined, and nobody can maintain fifty of
// them as they change. What the mechanic actually knows is "the rate
// I charge out at the Henderson place" — so they name it and save it.
//
// Same shape and same home as labor presets (the business profile
// doc), for the same reason: it's a small list the owner curates, not
// data anyone else needs.
//
// PARTS AND LABOR ARE SEPARATE RATES BECAUSE THEY GENUINELY ARE.
// Plenty of states tax parts as goods and exempt labor as a service;
// others tax both; a few tax neither. One combined rate would be
// wrong in most of the country, so both live on every preset and
// both default to zero — charging no tax is always the safe default,
// because it's the state of the app today.
// ═══════════════════════════════════════════════════════════════

// A rate is a percentage: 5.5 means 5.5%. Stored as the number a
// human would say out loud, not as a 0.055 multiplier — the value in
// the input box and the value in the database are the same thing, so
// there's no unit to get backwards.
//
// Capped at 100 because a typo of 55 for 5.5 is recoverable and a
// typo of 5500 charging fifty-five times the job is not.
export const MAX_TAX_RATE = 100;

export function normalizeRate(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  // Two decimals is enough for every real rate (7.25, 8.375 rounds to
  // 8.38 — within a cent on a $1,000 job).
  return Math.min(Math.round(n * 100) / 100, MAX_TAX_RATE);
}

// Shape:
//   { id, label, partsRate, laborRate, taxSupplies }
//
// taxSupplies rides on the preset rather than on the job because
// whether shop supplies are taxable is a property of the
// jurisdiction, not of the day. Most states treat them as goods and
// tax them at the parts rate, which is why it defaults on.
export function normalizeTaxPresets(presets) {
  return (presets || [])
    .map((p) => ({
      id: p?.id || '',
      label: (p?.label || '').trim().slice(0, 60),
      partsRate: normalizeRate(p?.partsRate),
      laborRate: normalizeRate(p?.laborRate),
      taxSupplies: p?.taxSupplies !== false,
    }))
    .filter((p) => p.label);
}

// No built-in defaults, unlike labor presets. There is no tax rate
// that is right for everyone, and a wrong one that looks official is
// worse than an empty list.
export function resolveTaxPresets(presets) {
  return normalizeTaxPresets(presets);
}

export function blankTaxPreset(id) {
  return { id, label: '', partsRate: 0, laborRate: 0, taxSupplies: true };
}

// How a preset reads on a chip or a row: "Lancaster · parts 5.5% ·
// labor 0%". Says both rates always, including the zeros — "labor 0%"
// is information (labor is exempt here), and hiding it would leave
// the reader wondering whether it was forgotten.
export function taxPresetSummary(preset) {
  if (!preset) return '';
  return `parts ${formatRate(preset.partsRate)} · labor ${formatRate(preset.laborRate)}`;
}

export function formatRate(rate) {
  const n = normalizeRate(rate);
  if (n === 0) return '0%';
  // Trim a trailing zero so 5.50 reads as 5.5%, but keep 8.38 whole.
  return `${String(Number(n.toFixed(2)))}%`;
}

// Does this preset actually charge anything? A preset with both rates
// at zero is legitimate (a jurisdiction that taxes neither), but it
// shouldn't render a tax block full of $0.00 lines on the customer's
// estimate.
export function presetChargesTax(preset) {
  return normalizeRate(preset?.partsRate) > 0 || normalizeRate(preset?.laborRate) > 0;
}
